"use client";

import React from "react";
import type { LucideIcon } from "lucide-react";
import { Star } from "lucide-react";
import { cn } from "~/lib/utils";

const ratingVariants = {
  default: {
    star: "text-foreground",
    emptyStar: "text-muted-foreground",
  },
  destructive: {
    star: "text-red-500",
    emptyStar: "text-red-200",
  },
  yellow: {
    star: "text-yellow-500",
    emptyStar: "text-yellow-200",
  },
  brand: {
    star: "text-brand",
    emptyStar: "text-brand/30",
  },
  disabled: {
    star: "text-muted-foreground",
    emptyStar: "text-muted-foreground",
  },
};

interface RatingsProps extends React.HTMLAttributes<HTMLDivElement> {
  rating: number;
  totalStars?: number;
  size?: number;
  fill?: boolean;
  Icon?: LucideIcon;
  variant?: keyof typeof ratingVariants;
  showText?: boolean;
  onRatingChange?: (rating: number) => void;
  disabled?: boolean;
}

export const CommentRatings = ({
  rating,
  totalStars = 5,
  size = 20,
  fill = true,
  Icon = Star,
  variant = "default",
  showText = true,
  onRatingChange,
  disabled,
  className,
  ...props
}: RatingsProps) => {
  const handleSelect = (index: number) => {
    onRatingChange?.(index);
  };

  const displayRating = rating;
  const fullStars = Math.floor(displayRating);
  const partialStar =
    displayRating % 1 > 0 ? (
      <PartialStar
        fillPercentage={displayRating % 1}
        size={size}
        className={cn(ratingVariants[variant].star)}
        Icon={Icon}
      />
    ) : null;

  if (onRatingChange) {
    return (
      <div
        role="group"
        className={cn("flex w-fit items-center gap-2", className)}
        {...props}
      >
        <div className="flex items-center">
          {Array.from({ length: totalStars }, (_, index) => (
            <button
              key={index}
              data-star-index={index + 1}
              type="button"
              aria-label={`Ocena ${index + 1} zvezdica`}
              aria-pressed={rating === index + 1}
              className="focus-visible:ring-ring flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-md focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed"
              disabled={disabled}
              onClick={() => handleSelect(index + 1)}
            >
              <Icon
                size={size}
                className={cn(
                  "stroke-1",
                  index < rating
                    ? ratingVariants[variant].star
                    : variant === "brand"
                      ? "text-brand/80"
                      : ratingVariants[variant].emptyStar,
                  fill && index < rating && "fill-current",
                )}
              />
            </button>
          ))}
        </div>
        {showText && (
          <span
            aria-live="polite"
            className="text-muted-foreground whitespace-nowrap tabular-nums"
          >
            {rating === 0
              ? "Oceni sesiju"
              : `${rating.toFixed(1)}/${totalStars}`}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex w-fit items-center gap-2", className)} {...props}>
      <div
        className="flex items-center"
        role="img"
        aria-label={`Ocena ${rating.toFixed(1)} od ${totalStars}`}
      >
        {Array.from({ length: fullStars }, (_, index) => (
          <Icon
            key={index}
            size={size}
            className={cn(
              fill ? "fill-current stroke-1" : "fill-transparent",
              ratingVariants[variant].star,
            )}
          />
        ))}
        {partialStar}
        {Array.from(
          {
            length: Math.max(0, totalStars - fullStars - (partialStar ? 1 : 0)),
          },
          (_, index) => (
            <Icon
              key={index}
              size={size}
              className={cn("stroke-1", ratingVariants[variant].emptyStar)}
            />
          ),
        )}
      </div>
      {showText && (
        <span className="text-muted-foreground min-w-[10ch] text-left whitespace-nowrap tabular-nums sm:min-w-[13ch]">
          {rating === 0 ? "Oceni sesiju" : `${rating.toFixed(1)}/${totalStars}`}
        </span>
      )}
    </div>
  );
};

interface PartialStarProps {
  fillPercentage: number;
  size: number;
  className?: string;
  Icon: LucideIcon;
}

const PartialStar = ({
  fillPercentage,
  size,
  className,
  Icon,
}: PartialStarProps) => {
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <Icon size={size} className={cn("fill-transparent", className)} />
      <div
        style={{
          position: "absolute",
          top: 0,
          overflow: "hidden",
          width: `${fillPercentage * 100}%`,
        }}
      >
        <Icon size={size} className={cn("fill-current", className)} />
      </div>
    </div>
  );
};
